/**
 * In-memory access-token store for the JWT Bearer auth.
 *
 * Holds the short-lived access token the SPA obtains from
 * `POST /auth/token/refresh` and attaches as `Authorization: Bearer <token>` to
 * its authenticated requests (see `lib/fetch`). The token lives ONLY in this
 * module's closure for the lifetime of the page — never in `localStorage`,
 * `sessionStorage` or a cookie — so it cannot be read by other scripts and is
 * gone the moment the tab closes. The long-lived refresh material stays in the
 * httpOnly refresh cookie the backend manages; this module never sees it.
 *
 * The state is process-global on purpose: `fetch` (a free function) and the app
 * bootstrap both need the same token, and there is exactly one token per page.
 */

/** The current access token, or `null` when the SPA is not (yet) authenticated. */
let accessToken: string | null = null;

/**
 * Epoch milliseconds at which the current access token expires, or `null` when
 * there is no token. Derived from the `expiresIn` (seconds) the refresh endpoint
 * returns. Kept for callers that want to refresh proactively; the fetch layer's
 * 401-retry does not depend on it (a stale token simply 401s and is refreshed).
 */
let expiresAt: number | null = null;

/** Return the current access token, or `null` when none is held. */
export function getAccessToken(): string | null {
  return accessToken;
}

/** Return the access token's expiry (epoch ms), or `null` when none is held. */
export function getAccessTokenExpiry(): number | null {
  return expiresAt;
}

/**
 * Store a freshly issued access token.
 *
 * @param token the access token from `POST /auth/token/refresh`
 * @param expiresIn the token lifetime in SECONDS (the backend's `expiresIn`)
 */
export function setAccessToken(token: string, expiresIn: number): void {
  accessToken = token;
  expiresAt = Number.isFinite(expiresIn) ? Date.now() + expiresIn * 1000 : null;
}

/** Drop the in-memory access token (logout, or a refresh that 401s). */
export function clearAccessToken(): void {
  accessToken = null;
  expiresAt = null;
}
